import Ajv from 'ajv';
import { z } from 'zod';
import { SchemaDate, SchemaTimestamp, ZodArrayToEnum } from '@axiom/types';
import {
  Opportunity,
  TimeCommitment,
  PositionsConst,
  EngagementLengthConst,
  CountryCodesConst,
  CandidateOpportunitiesConst,
  WorkFeedConst,
} from '@axiom/const';

import { axiomValidationOptions } from './options';
import { AccountSchema } from './account';
import { PositionSchema } from './position';
import { CandidateWorkFeedOpportunitySchema } from './candidate-work-feed-opportunity';
import { ZodCandidateOpportunityCandidateStatuses } from './candidate-opportunities-candidate-statuses';

const { Phases } = WorkFeedConst;
const { Worksites } = PositionsConst;
const { EngagementLengths } = EngagementLengthConst;

const PhasesValues = Object.values(Phases);

const EngagementLengthValues = Object.values(EngagementLengths);

const TimeCommitmentValues = Object.values(TimeCommitment.HoursPerWeek);

const WorksitesValues = Object.values(Worksites);

export const CandidateWorkFeedCountryCodes = Object.keys(
  CountryCodesConst.CountryCodesAbbreviations
) as Array<keyof typeof CountryCodesConst.CountryCodesAbbreviations>;

export const CandidateWorkFeedItemSchema = z.object({
  account: AccountSchema.partial()
    .pick({
      calculatedName: true,
      website: true,
    })
    .nullable(),
  addedToPoolAt: SchemaTimestamp.nullable(),
  candidateIsInterested: z.boolean().default(false),
  candidateStatus: ZodCandidateOpportunityCandidateStatuses.nullable(),
  id: z.string().uuid(),
  isNotAvailableOnCandidateFeed: z.boolean().default(false),
  isSaved: z.boolean().default(false),
  opportunity: CandidateWorkFeedOpportunitySchema,
  phase: z.enum(ZodArrayToEnum(PhasesValues)),
  phaseInterviewingDate: SchemaDate.nullable(),
  phaseSubmittedDate: SchemaDate.nullable(),
  position: PositionSchema.partial().pick({
    billingHoursPerWeek: true,
    billingUnitsPerWeek: true,
    businessTitle: true,
    description: true,
    endDate: true,
    estimatedEngagementTotalMonths: true,
    id: true,
    isPremium: true,
    name: true,
    role: true,
    startDate: true,
    weeklyTimeUnit: true,
    worksite: true,
  }),
  rejectionLossCode: z
    .enum(
      ZodArrayToEnum(
        CandidateOpportunitiesConst.CandidateRejectionLossCodesValues
      )
    )
    .nullable(),
  rejectionLossCodeCustomMessage: z.string().nullable(),
  runningCandidateInterestedCount: z.number().nonnegative().int().default(0),
  runningCandidateInterviewingCount: z.number().nonnegative().int().default(0),
  runningCandidateSubmittedCount: z.number().nonnegative().int().default(0),
});

export type CandidateWorkFeedItem = z.infer<typeof CandidateWorkFeedItemSchema>;

export const CandidateWorkFeedFormFilterSchema = z.object({
  businessTitles: z.array(z.string()).nullish(),
  countries: z
    .array(z.enum(ZodArrayToEnum(CandidateWorkFeedCountryCodes)))
    .nullish(),
  engagementLength: z
    .array(z.enum(ZodArrayToEnum(EngagementLengthValues)))
    .nullish(),
  industries: z.array(z.string()).nullish(),
  opportunityPhases: z.array(z.enum(ZodArrayToEnum(PhasesValues))).nullish(),
  practiceAreas: z.array(z.string()).nullish(),
  focusAreas: z.array(z.string()).nullish(),
  timeCommitment: z
    .array(z.enum(ZodArrayToEnum(TimeCommitmentValues)))
    .nullish(),
  worksites: z.array(z.enum(ZodArrayToEnum(WorksitesValues))).nullish(),
});

export type CandidateWorkFeedFormFilter = z.infer<
  typeof CandidateWorkFeedFormFilterSchema
>;

export const CandidateWorkFeedFilterSchema = z.object({
  filters: CandidateWorkFeedFormFilterSchema.nullish(),
  page: z.number().nonnegative().int().default(1),
  pageSize: z.number().nonnegative().int().default(10),
});

export type CandidateWorkFeedFilter = z.infer<
  typeof CandidateWorkFeedFilterSchema
>;

export const CandidateWorkFeedSearchSchema = z.object({
  meta: z.object({
    currentPage: z.number().nonnegative().int(),
    pageCount: z.number().nonnegative().int(),
    resultCount: z.number().nonnegative().int(),
    aggregations: z.number().nonnegative().int().nullable(),
  }),
  data: z.array(CandidateWorkFeedItemSchema).nullish(),
});

export type CandidateWorkFeedSearch = z.infer<
  typeof CandidateWorkFeedSearchSchema
>;

export const CandidateWorkFeedInterestedCountSchema = z.object({
  interestedCount: z.number().nonnegative().int(),
});

export type CandidateWorkFeedInterestedCount = z.infer<
  typeof CandidateWorkFeedInterestedCountSchema
>;

export const CandidateWorkFeedStatisticsSchema = z.object({
  candidateId: z.string().uuid(),
  roundedTotal: z.number().nonnegative().int(),
});

export type CandidateWorkFeedStatistics = z.infer<
  typeof CandidateWorkFeedStatisticsSchema
>;

export const CandidateWorkFeedHideSchema = z.object({
  opportunityId: z.string().uuid(),
});

export type CandidatesHiddenOpportunity = z.infer<
  typeof CandidateWorkFeedHideSchema
>;

const ajvNoCoerce = new Ajv({
  ...axiomValidationOptions(),
});

// Validations
const candidateWorkFeedPageSizeValidation = {
  type: 'number',
  maximum: 100,
  minimum: 1,
  name: 'pageSize',
};

// Validators
export const candidateWorkFeedPageSizeValidator = ajvNoCoerce.compile(
  candidateWorkFeedPageSizeValidation
);

export const candidateWorkFeedSortTermValidator = ajvNoCoerce.compile({
  type: 'object',
  additionalProperties: false,
  properties: {
    sort: {
      type: 'string',
      enum: [`opportunity.${Opportunity.CREATED_AT}`],
    },
  },
});
